/**
 * Test ID — see GLOSSARY.md → Trial Authoring → Test ID.
 *
 * The Master Template handle for a Test Case (the prefix abbreviates the
 * Function; e.g. AS-01 ↔ "Add Sites"). It is an EXTERNAL, human-authored
 * identifier, so its shape is only loosely structured — a `<prefix>-<number>`
 * where the prefix is alphanumeric and may contain a slash. Real catalogues
 * carry e.g. `AS-01`, `MCDOS-01`, `PCV1-03`, `PBV1W26-01`, `RAND/ENRL-03`,
 * `Week 0-01`, so the prefix is NOT letters-only and is longer than five chars.
 * (The earlier `[A-Z]{2,5}` form was an over-fit to a sample and rejected real
 * Master-Template handles end-to-end — the T064 cutover smoke caught it.)
 *
 * Source data also contains whitespace artefacts (e.g. `"ENRL -02"` with a stray
 * space). The factory strips ALL whitespace before validating, collapsing such
 * variants to one canonical form for the extractor's pure-equality matching
 * (GLOSSARY → Test ID), so callers don't need to pre-normalise.
 */
export type TestId = string & { readonly __brand: 'TestId' };

// `<prefix>-<number>` after whitespace is stripped: prefix = 1–16 alphanumeric
// or `/` chars (mixed case); suffix = 1–3 digits.
const TEST_ID_PATTERN = /^[A-Za-z0-9/]{1,16}-\d{1,3}$/;

export const createTestId = (raw: string): TestId => {
  const normalised = raw.replace(/\s+/g, '');
  if (!TEST_ID_PATTERN.test(normalised)) {
    throw new Error(
      `Invalid TestId: ${JSON.stringify(raw)} (expected <prefix>-<number>, e.g. AS-01)`,
    );
  }
  return normalised as TestId;
};

// The prefix is everything before the final `-<digits>` group (after whitespace strip).
const PREFIX_PATTERN = /^(.+)-\d{1,3}$/;

/**
 * The alpha(-numeric) prefix of a Test ID handle — everything before the final
 * `-<digits>` group, after whitespace is stripped: `DO-04` → `DO`,
 * `RAND/ENRL-03` → `RAND/ENRL`, `NOV-07` → `NOV`. A handle that does not end in
 * `-<digits>` (not expected for a valid TestId) falls back to the stripped
 * handle and never throws — this is a presentation helper, not a validator.
 * Used by the Displayed Test ID derivation (see `display-test-id.ts`).
 */
export const testIdPrefix = (testId: string): string => {
  const normalised = testId.replace(/\s+/g, '');
  return PREFIX_PATTERN.exec(normalised)?.[1] ?? normalised;
};
